import { useMutation } from '@apollo/client';
import { AdminButton } from '@website/components/admin/AdminButton';
import {
  GlobalCurrencies,
  RemoveUserCreditInput,
  RemoveUserCreditsDocument,
} from '@website/types/graphqlOperations';
import { useState } from 'react';
import { Form, FormControl, FormGroup, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import PriceFormat from '../../utilities/PriceFormat';

interface CreditProps {
  userID: string;
  credit: {
    amountZD: number;
    currency: string;
  };
  refreshUser: () => void;
}

/**
 * Single currency credit
 *
 * Shows the user credit row for a specific currency (as we can have e.g. £100 and $100 of credit which must be shown
 * separately).
 */
export default function Credit({ userID, credit, refreshUser }: CreditProps) {
  const [removeCredit, { loading: isRemovingCredits }] = useMutation(
    RemoveUserCreditsDocument,
    {
      onError: (error) => {
        console.log(error);
        alert(error);
      },
      onCompleted: () => {
        refreshUser();
      },
    },
  );

  const [show, setShow] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<RemoveUserCreditInput>({
    mode: 'onBlur',
    defaultValues: {
      userID,
      currency: credit.currency as GlobalCurrencies,
    },
  });

  const onSubmit = async (data: RemoveUserCreditInput) => {
    await removeCredit({
      variables: {
        input: data,
      },
    });
    setShow(false);
  };

  return (
    <>
      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            Remove Credits -{' '}
            <PriceFormat
              amount={credit.amountZD}
              currencyIso={credit.currency}
            />{' '}
            (Admin Only)
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <FormGroup controlId="formBasicEmail">
              <Form.Label>Note</Form.Label>
              <FormControl
                as="textarea"
                placeholder="Enter note"
                name="note"
                required
                {...register('note', { required: true })}
              />
            </FormGroup>
          </Modal.Body>
          <Modal.Footer>
            <AdminButton disabled={!isValid} type="submit" variant="danger">
              {isRemovingCredits ? 'Removing' : 'Remove'}
            </AdminButton>
          </Modal.Footer>
        </Form>
      </Modal>
      <tr>
        <td>{credit.currency}</td>
        <td>
          <PriceFormat amount={credit.amountZD} currencyIso={credit.currency} />
        </td>
        <td>
          <AdminButton
            disabled={isRemovingCredits}
            onClick={() => setShow(true)}
          >
            {isRemovingCredits ? 'Removing' : 'Remove'}
          </AdminButton>
        </td>
      </tr>
    </>
  );
}
